// src/components/ui/AdvancedSearchForm.jsx
import React, { useMemo } from 'react';
import { ChevronDownIcon, XIcon } from '../common/IconsComponent';
import { useSearch } from '../../contexts/SearchContext';

// ─── Constants ────────────────────────────────────────────────────────────────

const AGE_GROUPS = [
  { value: '',             label: 'כל הגילאים (ברירת מחדל)' },
  { value: 'kids-6-9',     label: 'ילדים (6–9)' },
  { value: 'kids-10-12',   label: 'ילדים (10–12)' },
  { value: 'middle-school',label: 'חטיבת ביניים (ז–ט)' },
  { value: 'high-school',  label: 'תיכון (י–יב)' },
  { value: 'college',      label: 'סטודנטים' },
  { value: 'adults',       label: 'מבוגרים' },
  { value: 'all',          label: 'כל הגילאים (מתויג בקורס)' },
];

const COURSE_TYPES = [
  {
    value: 'private',
    label: 'שיעורים פרטיים',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
      </svg>
    ),
  },
  {
    value: 'group',
    label: 'שיעורים בקבוצות',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
          d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
      </svg>
    ),
  },
];

// ─── Sub-components ───────────────────────────────────────────────────────────

/** Single radio card for course type selection */
const CourseTypeCard = ({ type, isSelected, onChange }) => (
  <label
    className={`
      flex flex-col items-center justify-center gap-2 p-3 sm:p-4
      border-2 rounded-xl cursor-pointer
      transition-all duration-150 select-none
      ${isSelected
        ? 'border-indigo-500 bg-indigo-50 shadow-sm'
        : 'border-gray-200 bg-white hover:border-indigo-200 hover:bg-gray-50'
      }
    `}
  >
    <span className={`transition-colors ${isSelected ? 'text-indigo-500' : 'text-gray-400'}`}>
      {type.icon}
    </span>
    <span className={`text-xs sm:text-sm font-medium transition-colors ${isSelected ? 'text-indigo-700' : 'text-gray-600'}`}>
      {type.label}
    </span>
    <input
      type="radio"
      name="courseType"
      value={type.value}
      className="sr-only"
      checked={isSelected}
      onChange={onChange}
    />
  </label>
);

/** Consistent styled select with chevron */
const StyledSelect = ({ value, onChange, disabled, children }) => (
  <div className="relative">
    <select
      value={value}
      onChange={onChange}
      disabled={disabled}
      className="
        w-full bg-white border-2 border-gray-200 rounded-xl
        py-2.5 pr-4 pl-9 text-sm text-gray-700
        appearance-none text-right
        focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400
        hover:border-gray-300 transition-colors duration-150
        disabled:opacity-50 disabled:cursor-not-allowed
      "
    >
      {children}
    </select>
    {/* Chevron sits on the left (LTR side) in an RTL layout */}
    <span className="pointer-events-none absolute inset-y-0 left-2.5 flex items-center text-gray-400">
      <ChevronDownIcon size={15} />
    </span>
  </div>
);

/** Section label */
const FieldLabel = ({ children }) => (
  <p className="text-sm font-medium text-gray-700 mb-2">{children}</p>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const DEFAULT_FILTERS = {
  courseTypes:      [],
  educationalLevel: '',
  subject:          '',
  ageGroup:         '',
  priceRange:       [50, 500],
};

const AdvancedSearchForm = ({ filters, setFilters, handleSearch, onAfterSearch }) => {
  const { subjects, subjectsLoading, subjectsError } = useSearch();

  const subjectOptions = useMemo(
    () => (subjects || []).map((s) => ({ value: s.name, label: s.name })),
    [subjects]
  );

  // Single-select: selecting a type deselects the other
  const handleCourseTypeChange = (type) => {
    setFilters((prev) => ({ ...prev, courseTypes: [type] }));
  };

  const isCourseTypeSelected = (type) =>
    filters.courseTypes.length === 1 && filters.courseTypes[0] === type;

  const onReset = () => setFilters(DEFAULT_FILTERS);

  const onSubmit = (e) => {
    e.preventDefault();
    handleSearch();
    onAfterSearch?.();
  };

  return (
    <form
      onSubmit={onSubmit}
      dir="rtl"
      className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden"
    >
      {/* ── Header ──────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between px-4 sm:px-5 py-3 sm:py-4 border-b border-gray-100 bg-gradient-to-l from-indigo-50/60 to-white">
        <div>
          <h3 className="text-sm sm:text-base font-semibold text-gray-800">חיפוש מתקדם</h3>
          {subjectsLoading && (
            <p className="text-xs text-gray-400 mt-0.5">טוען מקצועות…</p>
          )}
          {!!subjectsError && (
            <p className="text-xs text-red-500 mt-0.5">{subjectsError}</p>
          )}
        </div>

        {/* Accent dot */}
        <div className="w-2 h-2 rounded-full bg-indigo-400 opacity-60" aria-hidden />
      </div>

      {/* ── Fields ──────────────────────────────────────────────────── */}
      {/*
          Mobile  (default): single column, fields stacked
          Tablet  (sm):      two columns
          Desktop (lg):      three columns side-by-side
      */}
      <div className="p-4 sm:p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">

        {/* Course Type */}
        <div>
          <FieldLabel>סוג קורס</FieldLabel>
          <div className="grid grid-cols-2 gap-2">
            {COURSE_TYPES.map((type) => (
              <CourseTypeCard
                key={type.value}
                type={type}
                isSelected={isCourseTypeSelected(type.value)}
                onChange={() => handleCourseTypeChange(type.value)}
              />
            ))}
          </div>
        </div>

        {/* Subject */}
        <div>
          <FieldLabel>מקצוע</FieldLabel>
          <StyledSelect
            value={filters.subject}
            onChange={(e) => setFilters((prev) => ({ ...prev, subject: e.target.value }))}
            disabled={subjectsLoading}
          >
            <option value="">בחר מקצוע</option>
            {subjectOptions.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </StyledSelect>
        </div>

        {/* Age Group */}
        <div>
          <FieldLabel>קהל יעד (קבוצת גיל)</FieldLabel>
          <StyledSelect
            value={filters.ageGroup ?? ''}
            onChange={(e) => setFilters((prev) => ({ ...prev, ageGroup: e.target.value }))}
          >
            {AGE_GROUPS.map((a) => (
              <option key={a.value || 'any'} value={a.value}>{a.label}</option>
            ))}
          </StyledSelect>
        </div>

      </div>

      {/* ── Actions ─────────────────────────────────────────────────── */}
      <div className="
        px-4 sm:px-5 py-3 sm:py-4
        border-t border-gray-100 bg-gray-50/60
        flex flex-col-reverse xs:flex-row items-stretch xs:items-center justify-between
        gap-2.5
      ">
        <button
          type="button"
          onClick={onReset}
          className="
            inline-flex items-center justify-center gap-1.5
            text-xs sm:text-sm font-medium text-gray-500
            hover:text-gray-700 transition-colors
            py-2 px-3 rounded-lg hover:bg-gray-100
          "
        >
          <XIcon size={13} />
          נקה פילטרים
        </button>

        <button
          type="submit"
          className="
            inline-flex items-center justify-center
            bg-gradient-to-r from-indigo-600 to-purple-600
            text-white text-sm font-semibold
            py-2.5 px-6 rounded-xl shadow-sm
            hover:opacity-90 active:scale-[0.98]
            transition-all duration-150
            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 focus-visible:ring-offset-2
          "
        >
          חפש קורסים
        </button>
      </div>
    </form>
  );
};

export default AdvancedSearchForm;